import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Video, Plus, Trash2, X, Search, Check, ExternalLink, Edit2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

const VideoLibrary = () => {
  const [videos, setVideos] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ youtube_url: "", title: "", exercise_ids: [] });
  const [saving, setSaving] = useState(false);
  const [exSearch, setExSearch] = useState("");
  const [expandedVideo, setExpandedVideo] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [vRes, eRes] = await Promise.all([
        fetch(`${API}/videos/`, { credentials: "include" }),
        fetch(`${API}/coach/exercises`, { credentials: "include" }),
      ]);
      const vData = await vRes.json();
      const eData = await eRes.json();
      setVideos(vData.videos || []);
      setExercises(eData.exercises || []);
    } catch {
      toast.error("Failed to load videos");
    } finally {
      setLoading(false);
    }
  };

  const extractYouTubeId = (url) => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : "";
  };

  const resetForm = () => {
    setForm({ youtube_url: "", title: "", exercise_ids: [] });
    setEditing(null);
    setShowForm(false);
    setExSearch("");
  };

  const handleEdit = (video) => {
    setForm({
      youtube_url: video.youtube_url,
      title: video.title,
      exercise_ids: [...video.exercise_ids],
    });
    setEditing(video.video_id);
    setShowForm(true);
    setExSearch("");
  };

  const handleSave = async () => {
    if (!form.youtube_url || !form.title) {
      toast.error("Please fill in URL and title");
      return;
    }
    setSaving(true);
    try {
      const url = editing ? `${API}/videos/${editing}` : `${API}/videos/`;
      const method = editing ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      toast.success(editing ? "Video updated" : "Video added");
      resetForm();
      fetchData();
    } catch {
      toast.error("Failed to save video");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm("Delete this video?")) return;
    try {
      await fetch(`${API}/videos/${videoId}`, { method: "DELETE", credentials: "include" });
      toast.success("Video deleted");
      setVideos(prev => prev.filter(v => v.video_id !== videoId));
    } catch {
      toast.error("Failed to delete");
    }
  };

  const toggleExercise = (exId) => {
    setForm(prev => ({
      ...prev,
      exercise_ids: prev.exercise_ids.includes(exId)
        ? prev.exercise_ids.filter(id => id !== exId)
        : [...prev.exercise_ids, exId],
    }));
  };

  const filteredExercises = useMemo(() => {
    if (!exSearch) return exercises;
    const q = exSearch.toLowerCase();
    return exercises.filter(e => (e.exercise_name || "").toLowerCase().includes(q));
  }, [exercises, exSearch]);

  // Build exercise lookup
  const exLookup = useMemo(() => {
    const map = {};
    exercises.forEach(e => { map[e.exercise_id] = e.exercise_name; });
    return map;
  }, [exercises]);

  const previewId = extractYouTubeId(form.youtube_url);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Video Library</h2>
          <p className="text-sm text-gray-500">{videos.length} video{videos.length !== 1 ? "s" : ""} &bull; Link demo videos to exercises</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-xl font-medium text-sm transition-colors"
          data-testid="add-video-btn"
        >
          <Plus className="w-4 h-4" />
          Add Video
        </button>
      </div>

      {/* Form Modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-[#111827] border border-[#2D3B4F] rounded-2xl p-5 space-y-4"
          >
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-white">{editing ? "Edit Video" : "Add New Video"}</h3>
              <button onClick={resetForm} className="text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            {/* URL + Title */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">YouTube URL</label>
                <input
                  type="text"
                  value={form.youtube_url}
                  onChange={(e) => setForm(prev => ({ ...prev, youtube_url: e.target.value }))}
                  placeholder="https://youtube.com/watch?v=..."
                  className="w-full bg-[#0A0E17] border border-[#2D3B4F] rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 outline-none placeholder:text-gray-600"
                  data-testid="video-url-input"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Title</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g. Back Squat Form"
                  className="w-full bg-[#0A0E17] border border-[#2D3B4F] rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 outline-none placeholder:text-gray-600"
                  data-testid="video-title-input"
                />
              </div>
            </div>

            {/* YouTube Preview */}
            {previewId && (
              <div className="rounded-xl overflow-hidden bg-black aspect-video max-w-sm">
                <iframe
                  src={`https://www.youtube.com/embed/${previewId}`}
                  title="Preview"
                  className="w-full h-full"
                  allowFullScreen
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                />
              </div>
            )}

            {/* Exercise Selector */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">
                Linked Exercises ({form.exercise_ids.length} selected)
              </label>
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="text"
                  value={exSearch}
                  onChange={(e) => setExSearch(e.target.value)}
                  placeholder="Search exercises..."
                  className="w-full bg-[#0A0E17] border border-[#2D3B4F] rounded-lg pl-9 pr-3 py-2 text-white text-sm focus:border-blue-500 outline-none placeholder:text-gray-600"
                  data-testid="video-exercise-search"
                />
              </div>
              <div className="max-h-48 overflow-y-auto space-y-1 bg-[#0A0E17] border border-[#2D3B4F] rounded-xl p-2">
                {filteredExercises.length === 0 ? (
                  <p className="text-xs text-gray-600 text-center py-3">No exercises found</p>
                ) : (
                  filteredExercises.map(ex => {
                    const checked = form.exercise_ids.includes(ex.exercise_id);
                    return (
                      <button
                        key={ex.exercise_id}
                        type="button"
                        onClick={() => toggleExercise(ex.exercise_id)}
                        className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-left transition-colors ${
                          checked ? "bg-blue-500/15 text-blue-400" : "text-gray-400 hover:bg-[#141B2D]"
                        }`}
                        data-testid={`video-exercise-${ex.exercise_id}`}
                      >
                        <div className={`w-4 h-4 rounded flex-shrink-0 flex items-center justify-center border ${
                          checked ? "bg-blue-500 border-blue-500" : "border-gray-600"
                        }`}>
                          {checked && <Check className="w-2.5 h-2.5 text-white" />}
                        </div>
                        <span className="text-sm truncate">{ex.exercise_name}</span>
                        <span className="text-[10px] text-gray-600 ml-auto capitalize flex-shrink-0">{ex.category}</span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <button onClick={resetForm} className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors">Cancel</button>
              <button
                onClick={handleSave}
                disabled={saving || !form.youtube_url || !form.title}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white px-5 py-2 rounded-xl font-medium text-sm transition-colors"
                data-testid="video-save-btn"
              >
                {saving ? <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white" /> : <Check className="w-4 h-4" />}
                {editing ? "Update" : "Save"}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Video List */}
      {videos.length === 0 && !showForm ? (
        <div className="text-center py-16 text-gray-500">
          <Video className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">No videos yet</p>
          <p className="text-sm">Add demo videos and link them to exercises</p>
        </div>
      ) : (
        <div className="space-y-2">
          {videos.map((video) => {
            const ytId = video.youtube_id || extractYouTubeId(video.youtube_url);
            const isExpanded = expandedVideo === video.video_id;
            return (
              <div key={video.video_id} className="bg-[#111827] border border-[#1E293B] rounded-xl overflow-hidden" data-testid={`video-item-${video.video_id}`}>
                <div className="flex items-center gap-3 p-3">
                  {/* Thumbnail */}
                  {ytId ? (
                    <img
                      src={`https://img.youtube.com/vi/${ytId}/mqdefault.jpg`}
                      alt={video.title}
                      className="w-20 h-12 rounded-lg object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-20 h-12 rounded-lg bg-[#1E293B] flex items-center justify-center flex-shrink-0">
                      <Video className="w-5 h-5 text-gray-600" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-white text-sm truncate">{video.title}</h4>
                    <p className="text-xs text-gray-500">{video.exercise_ids?.length || 0} exercise{video.exercise_ids?.length !== 1 ? "s" : ""} linked</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setExpandedVideo(isExpanded ? null : video.video_id)}
                      className="p-2 text-gray-500 hover:text-white rounded-lg hover:bg-[#1E293B] transition-colors"
                      data-testid={`video-expand-${video.video_id}`}
                    >
                      <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleEdit(video)}
                      className="p-2 text-gray-500 hover:text-blue-400 rounded-lg hover:bg-[#1E293B] transition-colors"
                      data-testid={`video-edit-${video.video_id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(video.video_id)}
                      className="p-2 text-gray-500 hover:text-red-400 rounded-lg hover:bg-[#1E293B] transition-colors"
                      data-testid={`video-delete-${video.video_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {/* Expanded: linked exercises */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="px-3 pb-3 pt-1 border-t border-[#1E293B]">
                        <p className="text-[10px] text-gray-600 uppercase tracking-wider mb-2">Linked Exercises</p>
                        {video.exercise_ids?.length > 0 ? (
                          <div className="flex flex-wrap gap-1.5">
                            {video.exercise_ids.map(eid => (
                              <span key={eid} className="text-xs bg-blue-500/10 text-blue-400 px-2 py-1 rounded-lg">
                                {exLookup[eid] || eid}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-600">No exercises linked</p>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default VideoLibrary;
